/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import Modal from '../../components/Modal';
import { request } from '../../config/helpers/axios-instance';
import { toast } from 'react-toastify';

const ID_TYPES = [
    { value: 'id', label: 'Delivery ID' },
    { value: 'userOrderId', label: 'User Order ID' },
    { value: 'deliveryPartnerUniqueId', label: 'Partner Reference ID' },
];

const parseIds = (text) =>
    text.split(/[\n,]+/).map(s => s.trim()).filter(Boolean);

const BulkMarkDeliveredModal = ({ onClose, onSuccess }) => {
    const [idType, setIdType] = useState('id');
    const [inputText, setInputText] = useState('');
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState(null);

    const ids = parseIds(inputText);
    const isNumericType = idType === 'id' || idType === 'userOrderId';

    const handleSubmit = async () => {
        if (ids.length === 0) {
            toast.warn('No IDs entered');
            return;
        }
        if (ids.length > 50) {
            toast.warn('Maximum 50 IDs allowed per batch');
            return;
        }

        setLoading(true);
        setResults(null);

        try {
            const deliveries = ids.map(rawId => ({
                [idType]: isNumericType ? parseInt(rawId, 10) : rawId,
            }));

            const response = await request({
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                url: 'delivery/markDeliveredBulk',
                data: { deliveries },
            });

            if (response.data && response.data.success) {
                const { succeeded, failed } = response.data.data.summary;
                setResults(response.data.data);
                if (failed === 0) {
                    toast.success(`All ${succeeded} marked as delivered`);
                } else {
                    toast.warn(`${succeeded} marked as delivered, ${failed} failed`);
                }
                onSuccess();
            } else {
                toast.error(response.data?.err || 'Error marking deliveries as delivered');
            }
        } catch {
            toast.error('Error marking deliveries as delivered');
        }

        setLoading(false);
    };

    const getResultLabel = (r, index) => {
        if (idType === 'userOrderId') return `Order #${r.userOrderId || ids[index]}`;
        if (idType === 'deliveryPartnerUniqueId') return r.deliveryPartnerUniqueId || ids[index];
        return `Delivery #${r.id || ids[index]}`;
    };

    const getErrorMessage = (err) => {
        if (!err) return 'Failed';
        if (typeof err === 'string') return err;
        if (err.message) return err.message;
        return 'Failed';
    };

    return (
        <Modal title="Bulk Mark Delivered" onClose={onClose}>
            <div className="flex flex-col gap-5">
                <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">ID Type</p>
                    <div className="flex flex-wrap gap-5">
                        {ID_TYPES.map(({ value, label }) => (
                            <label key={value} className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="idType"
                                    value={value}
                                    checked={idType === value}
                                    onChange={() => { setIdType(value); setResults(null); }}
                                    className="accent-emerald-600"
                                />
                                <span className="text-sm text-gray-700">{label}</span>
                            </label>
                        ))}
                    </div>
                </div>

                <div>
                    <p className="text-sm font-medium text-gray-700 mb-1">
                        Paste IDs — one per line or comma-separated <span className="text-gray-400">(max 50)</span>
                    </p>
                    <textarea
                        className="w-full h-44 border border-gray-300 rounded-md p-3 text-sm font-mono resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        placeholder={'e.g.\n101\n102\n103'}
                        value={inputText}
                        onChange={(e) => { setInputText(e.target.value); setResults(null); }}
                    />
                    <div className="flex justify-between mt-1">
                        {ids.length > 0 ? (
                            <p className={`text-xs ${ids.length > 50 ? 'text-red-500' : 'text-gray-500'}`}>
                                {ids.length} ID{ids.length !== 1 ? 's' : ''} detected{ids.length > 50 ? ' — max 50 allowed' : ''}
                            </p>
                        ) : <span />}
                    </div>
                </div>

                <button
                    onClick={handleSubmit}
                    disabled={loading || ids.length === 0 || ids.length > 50}
                    className="bg-emerald-600 text-white-50 py-3 rounded-md font-semibold disabled:opacity-50 hover:bg-emerald-700 transition-colors"
                >
                    {loading ? 'Marking...' : `Mark ${ids.length > 0 ? ids.length + ' ' : ''}as Delivered`}
                </button>

                {results && (
                    <div className="border border-gray-200 rounded-md overflow-hidden">
                        <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex gap-4 text-sm">
                            <span className="text-gray-700">Total: <strong>{results.summary.total}</strong></span>
                            <span className="text-emerald-600">Succeeded: <strong>{results.summary.succeeded}</strong></span>
                            {results.summary.failed > 0 && (
                                <span className="text-red-600">Failed: <strong>{results.summary.failed}</strong></span>
                            )}
                        </div>
                        <div className="max-h-52 overflow-y-auto divide-y divide-gray-100">
                            {results.results.map((r, i) => (
                                <div
                                    key={i}
                                    className={`px-4 py-2 flex items-center justify-between text-sm ${r.success ? 'bg-white' : 'bg-red-50'}`}
                                >
                                    <span className="font-mono text-gray-700">{getResultLabel(r, i)}</span>
                                    {r.success
                                        ? <span className="text-emerald-600 font-medium">✓ Delivered</span>
                                        : <span className="text-red-500 text-xs">{getErrorMessage(r.err)}</span>
                                    }
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default BulkMarkDeliveredModal;
